import { useMemo, useState } from "react";
import { Search, Users, ArrowUpDown, Download, Phone, Trash2, X } from "lucide-react";
import { STAGES, StageBadge, Avatar, Panel, EmptyState, inputCls, btnSecondary, relTime, answerFor, slug } from "./crm";

/** Sticky action bar shown while recruits are ticked (admins only). */
function SelectionBar({ count, onClear, onDelete }) {
  const [confirm, setConfirm] = useState(false);
  if (!count) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#0b1528] bg-[#0b1528] px-4 py-2.5 text-sm text-white" data-testid="recruits-selection-bar">
      <span className="font-semibold"><span className="font-mono-num">{count}</span> selected</span>
      <div className="ml-auto flex items-center gap-2">
        {confirm ? (
          <>
            <span className="hidden sm:inline text-xs text-slate-300">Permanently delete {count} recruit{count === 1 ? "" : "s"}?</span>
            <button onClick={() => { onDelete(); setConfirm(false); }} data-testid="recruits-bulk-delete-confirm" className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700">Yes, delete</button>
            <button onClick={() => setConfirm(false)} data-testid="recruits-bulk-delete-cancel" className="rounded-md border border-white/20 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-white/10">Cancel</button>
          </>
        ) : (
          <>
            <button onClick={() => setConfirm(true)} data-testid="recruits-bulk-delete" className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"><Trash2 className="h-3.5 w-3.5" /> Delete selected</button>
            <button onClick={onClear} data-testid="recruits-selection-clear" className="rounded-md p-1.5 text-slate-300 hover:bg-white/10 hover:text-white" title="Clear selection"><X className="h-4 w-4" /></button>
          </>
        )}
      </div>
    </div>
  );
}

/** Inline two-step delete for a single row/card. */
function RowDelete({ id, confirmId, setConfirmId, onDelete, compact = false }) {
  if (confirmId === id) {
    return (
      <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => { onDelete(id); setConfirmId(null); }} data-testid={`recruit-delete-confirm-${id}`} className="rounded-md bg-rose-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-700">Delete</button>
        <button onClick={() => setConfirmId(null)} data-testid={`recruit-delete-cancel-${id}`} className="rounded-md border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
      </span>
    );
  }
  return (
    <button onClick={(e) => { e.stopPropagation(); setConfirmId(id); }} data-testid={`recruit-delete-${id}`} title="Delete recruit"
      className={`inline-flex items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600 ${compact ? "h-10 w-10 bg-slate-50 active:bg-rose-50" : "h-8 w-8"}`}>
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

export default function RecruitsView({ leads, agents, onOpen, onStage, onDelete, onDeleteMany, agentMode = false }) {
  const [query, setQuery] = useState("");
  const [stage, setStage] = useState("All");
  const [agent, setAgent] = useState("All");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState(() => new Set());
  const [confirmId, setConfirmId] = useState(null);
  const canDelete = !agentMode && !!onDelete;

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = leads.filter((l) => {
      const mq = !q || l.full_name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q) || l.phone.includes(q);
      const ms = stage === "All" || l.status === stage;
      const ma = agent === "All" || (agent === "none" ? !l.assigned_agent_id : l.assigned_agent_id === agent);
      return mq && ms && ma;
    });
    return list.sort((a, b) => {
      if (sort === "name") return a.full_name.localeCompare(b.full_name);
      if (sort === "oldest") return a.created_at.localeCompare(b.created_at);
      return b.created_at.localeCompare(a.created_at);
    });
  }, [leads, query, stage, agent, sort]);

  const visibleIds = rows.map((l) => l.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((prev) => { const n = new Set(prev); allVisibleSelected ? visibleIds.forEach((id) => n.delete(id)) : visibleIds.forEach((id) => n.add(id)); return n; });
  const deleteSelected = async () => { if (await onDeleteMany([...selected])) setSelected(new Set()); };
  const deleteOne = async (id) => { if (await onDelete(id)) setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; }); };

  const exportCsv = () => {
    const head = ["Name", "Email", "Phone", "Stage", "Agent", "Licensed", "Submitted"];
    const body = rows.map((l) => [l.full_name, l.email, l.phone, l.status, l.assigned_agent_name || "", answerFor(l, "licensed") || "", l.created_at]);
    const csv = [head, ...body].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `breeze-recruits-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-5" data-testid="recruits-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900">{agentMode ? "My Recruits" : "Recruits"}</h1>
          <p className="mt-1 text-sm text-slate-500"><span className="font-mono-num font-semibold text-slate-900">{rows.length}</span> of {leads.length} recruits</p>
        </div>
        <button onClick={exportCsv} data-testid="recruits-export-csv" className={`${btnSecondary} hidden sm:inline-flex`}><Download className="h-4 w-4" /> Export CSV</button>
      </div>

      {canDelete && <SelectionBar count={selected.size} onClear={() => setSelected(new Set())} onDelete={deleteSelected} />}

      <Panel className="overflow-hidden" testid="admin-leads-list">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-3 sm:p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, e-mail or phone…" data-testid="admin-search-input" className={`${inputCls} pl-9`} />
          </div>
          {/* Stage chips (phones) */}
          <div className="-mx-3 flex gap-1.5 overflow-x-auto px-3 no-scrollbar sm:hidden" data-testid="recruits-stage-chips">
            {["All", ...STAGES].map((s) => (
              <button key={s} onClick={() => setStage(s)} data-testid={`recruits-chip-${slug(s)}`} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${stage === s ? "border-[#0b1528] bg-[#0b1528] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{s}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
            <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="recruits-filter-stage" className={`${inputCls} hidden sm:block sm:w-auto`}>
              <option value="All">All stages</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {!agentMode && (
              <select value={agent} onChange={(e) => setAgent(e.target.value)} data-testid="recruits-filter-agent" className={`${inputCls} sm:w-auto`}>
                <option value="All">All agents</option>
                <option value="none">Unassigned</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            )}
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="recruits-sort" className={`${inputCls} sm:w-auto ${agentMode ? "col-span-2" : ""}`}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
        </div>

        {rows.length === 0 ? (
          <EmptyState icon={Users} title={leads.length ? "No recruits match your filters" : "No recruits yet"} hint={leads.length ? "Try clearing the search or filters." : agentMode ? "Recruits routed to you will appear here." : "New quiz submissions appear here and are auto-assigned round-robin."} testid="recruits-empty" />
        ) : (
          <>
          {/* Card list (phones) */}
          <ul className="divide-y divide-slate-100 sm:hidden" data-testid="recruits-card-list">
            {rows.map((l) => (
              <li key={l.id} className={`flex items-center gap-2 px-4 py-3.5 active:bg-slate-50 ${selected.has(l.id) ? "bg-sky-50/60" : ""}`}>
                {canDelete && (
                  <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleOne(l.id)} data-testid={`recruit-select-${l.id}`} aria-label={`Select ${l.full_name}`} className="h-5 w-5 shrink-0 rounded border-slate-300 accent-[#0b1528]" />
                )}
                <button onClick={() => onOpen(l.id)} data-testid={`recruit-card-${l.id}`} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                  <Avatar name={l.full_name} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[15px] font-semibold text-slate-900">{l.full_name}</span>
                      <span className="font-mono-num shrink-0 text-[11px] text-slate-400">{relTime(l.created_at)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                      <StageBadge stage={l.status} />
                      <span className="truncate">{agentMode ? l.email : l.assigned_agent_name || "Unassigned"}</span>
                    </div>
                  </div>
                </button>
                {confirmId !== l.id && <a href={`tel:${l.phone}`} data-testid={`recruit-card-call-${l.id}`} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-700 active:bg-sky-100"><Phone className="h-4 w-4" /></a>}
                {canDelete && <RowDelete id={l.id} confirmId={confirmId} setConfirmId={setConfirmId} onDelete={deleteOne} compact />}
              </li>
            ))}
          </ul>
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
                  {canDelete && (
                    <th className="w-10 px-4 py-3">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="recruits-select-all" aria-label="Select all visible recruits" className="h-4 w-4 rounded border-slate-300 accent-[#0b1528]" />
                    </th>
                  )}
                  <th className="px-5 py-3 text-left font-semibold">Recruit</th>
                  <th className="px-3 py-3 text-left font-semibold">Phone</th>
                  <th className="px-3 py-3 text-left font-semibold">Stage</th>
                  {!agentMode && <th className="px-3 py-3 text-left font-semibold">Agent</th>}
                  <th className="px-3 py-3 text-left font-semibold">Licensed</th>
                  <th className="px-5 py-3 text-right font-semibold"><span className="inline-flex items-center gap-1"><ArrowUpDown className="h-3 w-3" /> Submitted</span></th>
                  {canDelete && <th className="w-24 px-3 py-3 text-right font-semibold"><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((l) => {
                  const lic = answerFor(l, "licensed");
                  return (
                    <tr key={l.id} onClick={() => onOpen(l.id)} data-testid={`admin-lead-${l.id}`} className={`cursor-pointer transition-colors hover:bg-slate-50/80 ${selected.has(l.id) ? "bg-sky-50/60" : ""}`}>
                      {canDelete && (
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleOne(l.id)} data-testid={`recruit-select-${l.id}`} aria-label={`Select ${l.full_name}`} className="h-4 w-4 rounded border-slate-300 accent-[#0b1528]" />
                        </td>
                      )}
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={l.full_name} size="sm" />
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-slate-900">{l.full_name}</div>
                            <div className="truncate text-xs text-slate-500">{l.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="font-mono-num px-3 py-3 text-slate-700 whitespace-nowrap">{l.phone}</td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <select value={l.status} onChange={(e) => onStage(l.id, e.target.value)} data-testid={`row-stage-${l.id}`} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 focus:outline-none focus:border-sky-500">
                          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      {!agentMode && <td className="px-3 py-3 text-slate-700 whitespace-nowrap">{l.assigned_agent_name || <span className="text-slate-400">Unassigned</span>}</td>}
                      <td className="px-3 py-3">{lic ? <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${lic.toLowerCase().startsWith("yes") ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{lic}</span> : <span className="text-slate-400">—</span>}</td>
                      <td className="font-mono-num px-5 py-3 text-right text-xs text-slate-500 whitespace-nowrap">{relTime(l.created_at)}</td>
                      {canDelete && <td className="px-3 py-3 text-right whitespace-nowrap"><RowDelete id={l.id} confirmId={confirmId} setConfirmId={setConfirmId} onDelete={deleteOne} /></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Panel>
    </div>
  );
}
